import { ResponsivePie } from "@nivo/pie";
import { last } from "lodash";
import React, { useMemo, useState } from "react";

import { RouteComponentProps } from "react-router-dom";

import useTranslations from "../../hooks/useTranslations";

import {
  Avatar,
  Container,
  H4,
  Link,
  ScreenContainer,
  ScreenHeader,
} from "../wombat-ui";
import useGetReportAnswers from "../../graphql/hooks/queries/useGetReportAnswers";
import { getTalentUserData } from "../../utils/madUtils";
import { UserMinimalFragment } from "../../graphql/fragments";
import { AnalyticsIcon } from "../icons";

type EvaluationAnalyticsDetailProps = RouteComponentProps<{ id: string }>;

const EvaluationAnalyticsDetail: React.FC<EvaluationAnalyticsDetailProps> = ({
  match,
}) => {
  const t = useTranslations();
  const [selectedUsers, setSelectedUsers] = useState({
    users: [],
    option: undefined,
  });

  const { id } = match.params;
  const { data, loading } = useGetReportAnswers({
    variables: { templateQuestionDigest: [id] },
  });

  const question = useMemo(() => {
    if (data)
      return data.reportAnswers.collection?.[0]?.templateQuestion
        .label as string;
  }, [data]);

  const chartData = useMemo(() => {
    if (data) {
      const talentUserData = getTalentUserData(data.reportAnswers.collection);
      const mostRecentReportAnswer = last(
        last(talentUserData?.[0]?.reports).reportAnswers
      );

      return mostRecentReportAnswer.templateQuestion.options.map((o) => {
        const reportAnswers = talentUserData.filter(
          ({ reports }) =>
            last(last(reports).reportAnswers).value.selector === o.value
        );

        return {
          id: o.label,
          label: o.label,
          value: reportAnswers.length,
          users: reportAnswers.map((ra) => ra.user),
        };
      });
    }
    return null;
  }, [data]);

  return (
    <ScreenContainer loading={loading}>
      <ScreenHeader
        title={t("statistics.statistics")}
        description={t("statistics.statisticsDescription")}
        icon={AnalyticsIcon}
        actions={[
          <Link key={"back"} variant="button-subtle" to={"/statistics"}>
            {t("header.goBack")}
          </Link>,
        ]}
      ></ScreenHeader>

      <Container className="flex justify-between w-full max-h-96">
        <div className="w-full mx-12 mt-12 mb-6 min-h-lg">
          <div className="flex h-full px-3 py-2 bg-white rounded-md shadow-md">
            <div className="flex flex-col flex-grow">
              <H4>
                {t("statistics.collaboratorSurveyedOn", { value: question })}
              </H4>
              <ResponsivePie
                data={chartData}
                margin={{ top: 40, right: 80, bottom: 80, left: 80 }}
                innerRadius={0.5}
                padAngle={0.7}
                cornerRadius={3}
                colors={{ scheme: "nivo" }}
                borderWidth={1}
                borderColor={{ from: "color", modifiers: [["darker", 0.2]] }}
                radialLabelsSkipAngle={10}
                radialLabelsTextColor="#333333"
                radialLabelsLinkColor={{ from: "color" }}
                sliceLabelsSkipAngle={10}
                sliceLabelsTextColor="#333333"
                onClick={(data) =>
                  setSelectedUsers({
                    users: data.data.users as UserMinimalFragment[],
                    option: data.label,
                  })
                }
                legends={[
                  {
                    anchor: "bottom",
                    direction: "column",
                    justify: false,
                    translateX: 0,
                    translateY: 56,
                    
                    itemsSpacing: 5,
                    itemWidth: 100,
                    itemHeight: 18,
                    itemTextColor: "#999",
                    itemDirection: "left-to-right",
                    itemOpacity: 1,
                    symbolSize: 18,
                    symbolShape: "circle",
                    effects: [
                      {
                        on: "hover",
                        style: {
                          itemTextColor: "#000",
                        },
                      },
                    ],
                  },
                ]}
              />
            </div>
          </div>
          {selectedUsers.users.length > 0 && (
            <div className="flex flex-col px-4 py-3 mt-6 rounded-md shadow">
              <H4>
                {t("statistics.collaboratorAnswering", {
                  value: selectedUsers.option,
                })}
              </H4>

              <ul className="pt-3 divide-y divide-gray-200">
                {selectedUsers.users.map((su) => (
                  <li className="flex py-4">
                    <Avatar
                      src={su.avatar}
                      size={10}
                      alt={su.name}
                      type="user"
                    />
                    <div className="ml-3">
                      <p className="text-sm font-medium text-gray-900">
                        {su.name}
                      </p>
                      <p className="text-sm text-gray-500">
                        {selectedUsers.option}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Container>
    </ScreenContainer>
  );
};

export default React.memo(EvaluationAnalyticsDetail);
